import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Lock, Unlock, Download, X, User, Loader, Filter, ChevronDown, AlignJustify,
  Zap, DollarSign, Code, Copy, Check, ChevronRight, LogIn, Info, MoreVertical, Star,
  MessageCircle
} from 'lucide-react';
import CustomDropdown from './CustomDropdown';
import ModelLogo, { getCategoryIcon } from './ModelLogo';
import { useModels } from '../hooks/useModels';
import { useCategories } from '../hooks/useCategories';
import { useResults } from '../context/ResultsContext';
import VideoPlayer from './VideoPlayer';
import ComparisonCardActions from './ComparisonCardActions';
// HIDDEN: Comments feature disabled
// import CommentsModal from './CommentsModal';
import { useSearchParams, useNavigate } from 'react-router-dom';

import {
  trackVote,
  trackComparisonView,
  trackFilter,
  trackSort,
  trackPrivacyToggle,
  trackDownload
} from '../utils/GoogleAnalytics';
import { showToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const LOGIN_WALL_THRESHOLD = 10;

// ==========================================
// HELPER FUNCTIONS
// ==========================================

const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
};

const formatLatency = (ms) => {
  if (!ms || ms === 0) return '-';
  if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
  return Math.round(ms) + 'ms';
};

const formatCost = (cost) => {
  if (!cost || cost === 0) return '$0';
  if (cost < 0.001) return '<$0.001';
  if (cost < 0.01) return '$' + cost.toFixed(4);
  return '$' + cost.toFixed(3);
};


// ==========================================
// EXPANDABLE PROMPT COMPONENT
// ==========================================

function ExpandablePrompt({ prompt, maxLength = 100 }) {
  const [expanded, setExpanded] = useState(false);
  const needsExpansion = prompt && prompt.length > maxLength;

  const displayText = expanded || !needsExpansion
    ? prompt
    : prompt.substring(0, maxLength) + '...';

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 sm:p-3">
      <p className="text-sm font-medium text-gray-700 leading-relaxed break-words">
        {displayText || 'No prompt provided'}
        {needsExpansion && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="ml-2 text-blue-600 hover:text-blue-800 font-semibold text-xs inline-flex items-center"
          >
            {expanded ? 'Show less' : 'Show more'}
            <ChevronRight
              size={14}
              className={`ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
            />
          </button>
        )}
      </p>
    </div>
  );
}

// ==========================================
// COPY BUTTON COMPONENT
// ==========================================

function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`p-1.5 rounded bg-gray-800 hover:bg-gray-700 transition-colors ${className}`}
      aria-label={copied ? 'Code copied to clipboard' : 'Copy code to clipboard'}
      title={copied ? 'Copied!' : 'Copy code'}
    >
      {copied ? (
        <Check size={14} className="text-green-400" aria-hidden="true" />
      ) : (
        <Copy size={14} className="text-gray-400" aria-hidden="true" />
      )}
    </button>
  );
}

// ==========================================
// STATS TOGGLE BUTTON
// ==========================================

function StatsToggle({ showStats, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`h-10 flex items-center gap-2 px-4 rounded-xl border transition-all ${
        showStats
          ? 'bg-blue-50 border-blue-300 text-blue-700'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
      aria-label={showStats ? 'Hide statistics' : 'Show statistics'}
      aria-pressed={showStats}
      title={showStats ? 'Hide stats' : 'Show stats'}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="1" y="8" width="3" height="7" rx="1" fill="#93C5FD" />
        <rect x="6" y="4" width="3" height="11" rx="1" fill="#86EFAC" />
        <rect x="11" y="1" width="3" height="14" rx="1" fill="#FCA5A5" />
      </svg>
      <span className="text-sm font-medium">
        {showStats ? 'Stats ON' : 'Stats'}
      </span>
    </button>
  );
}

// ==========================================
// STATS OVERLAY
// ==========================================

function StatsOverlay({ results, category, findModel, isMobile = false }) {
  if (!results || results.length < 2) return null;

  const [result1, result2] = results;
  const model1Name = result1?.model || result1?.model_name || 'Model A';
  const model2Name = result2?.model || result2?.model_name || 'Model B';

  const isModelFailed = (result) => {
    if (!result) return true;

    // Only check the error field, not the response content
    const error = result.error || '';
    if (!error) return false;  // No error = success

    const errorLower = error.toLowerCase();

    const infrastructureKeywords = [
      'quota', 'rate_limit', 'rate limit', 'too many requests',
      'api_key', 'api key', 'unauthorized', 'authentication',
      'invalid key', 'not configured', 'timeout', 'connection',
      'service unavailable', 'server error', 'billing', 'credit'
    ];

    const contentPolicyKeywords = [
      'content policy', 'safety', 'blocked', 'inappropriate',
      'violation', 'refused', 'cannot generate', 'harmful'
    ];

    const modelFailureKeywords = ['error:', 'failed', 'exception'];

    const hasInfraError = infrastructureKeywords.some(kw => errorLower.includes(kw));
    const hasContentError = contentPolicyKeywords.some(kw => errorLower.includes(kw));
    const hasModelError = modelFailureKeywords.some(kw => errorLower.includes(kw));

    return hasInfraError || hasContentError || hasModelError || error.length > 0;
  };

  const model1Failed = isModelFailed(result1);
  const model2Failed = isModelFailed(result2);
  const anyFailed = model1Failed || model2Failed;

  const latency1 = result1?.latency_ms || (result1?.latency ? result1.latency * 1000 : 0);
  const latency2 = result2?.latency_ms || (result2?.latency ? result2.latency * 1000 : 0);
  const cost1 = result1?.cost || 0;
  const cost2 = result2?.cost || 0;

  const speedWinner = anyFailed ? 0 : (latency1 && latency2 ? (latency1 < latency2 ? 1 : latency2 < latency1 ? 2 : 0) : 0);
  const costWinner = anyFailed ? 0 : (cost1 && cost2 ? (cost1 < cost2 ? 1 : cost2 < cost1 ? 2 : 0) : 0);

  const getThirdMetric = () => {
    const isImageGen = category === 'IMAGE_GENERATION';
    const isVideoGen = category === 'IMAGE_TO_VIDEO' || category === 'VIDEO_GENERATION' || category === 'TEXT_TO_VIDEO';
    const isCodeGen = category === 'CODE_GENERATION';
    const isTranslation = category === 'MULTILINGUAL_TRANSLATION';

    if (isImageGen) {
      const res1 = result1?.resolution;
      const res2 = result2?.resolution;
      if (res1 || res2) {
        const getPixels = (res) => {
          if (!res) return 0;
          const match = res.match(/(\d+)x(\d+)/);
          return match ? parseInt(match[1]) * parseInt(match[2]) : 0;
        };
        const pixels1 = getPixels(res1);
        const pixels2 = getPixels(res2);
        const winner = anyFailed ? 0 : (pixels1 > pixels2 ? 1 : pixels2 > pixels1 ? 2 : 0);
        return { label: 'Size', value1: res1 || '-', value2: res2 || '-', icon: 'image', winner };
      }
      const status1 = result1?.image_url ? '✓' : '✗';
      const status2 = result2?.image_url ? '✓' : '✗';
      return { label: 'Status', value1: status1, value2: status2, icon: 'image', winner: 0 };
    }

    if (isVideoGen) {
      const dur1 = result1?.video_duration;
      const dur2 = result2?.video_duration;
      if (dur1 || dur2) {
        const winner = anyFailed ? 0 : (dur1 > dur2 ? 1 : dur2 > dur1 ? 2 : 0);
        return { label: 'Duration', value1: dur1 ? `${dur1}s` : '-', value2: dur2 ? `${dur2}s` : '-', icon: 'video', winner };
      }
      const status1 = result1?.video_url ? '✓' : '✗';
      const status2 = result2?.video_url ? '✓' : '✗';
      return { label: 'Status', value1: status1, value2: status2, icon: 'video', winner: 0 };
    }

    if (isCodeGen) {
      const lines1 = result1?.code_lines;
      const lines2 = result2?.code_lines;
      const winner = anyFailed ? 0 : (lines1 && lines2 ? (lines1 > lines2 ? 1 : lines2 > lines1 ? 2 : 0) : 0);
      return { label: 'Lines', value1: lines1 != null ? lines1 : '-', value2: lines2 != null ? lines2 : '-', icon: 'code', winner };
    }

    if (isTranslation) {
      const lang1 = result1?.detected_language?.toUpperCase()?.slice(0, 2);
      const lang2 = result2?.detected_language?.toUpperCase()?.slice(0, 2);
      if (lang1 || lang2) {
        return { label: 'Lang', value1: lang1 || '-', value2: lang2 || '-', icon: 'lang', winner: 0 };
      }
    }

    const words1 = result1?.word_count;
    const words2 = result2?.word_count;
    if (words1 != null || words2 != null) {
      const winner = anyFailed ? 0 : (words1 && words2 ? (words1 > words2 ? 1 : words2 > words1 ? 2 : 0) : 0);
      return { label: 'Words', value1: words1 != null ? words1 : '-', value2: words2 != null ? words2 : '-', icon: 'text', winner };
    }

    return null;
  };

  const thirdMetric = getThirdMetric();
  const thirdMetricWinner = thirdMetric?.winner || 0;

  const model1 = findModel ? findModel(model1Name) : null;
  const model2 = findModel ? findModel(model2Name) : null;

  const getCellStyle = (isWinner, isFailed = false) => {
    if (isFailed) return 'text-red-400';
    if (isWinner) return 'bg-green-100 text-green-700';
    return 'text-gray-600';
  };

  // Mobile version
  if (isMobile) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden mb-3">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="py-2 px-2 text-left w-24"></th>
              <th className="py-2 px-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={12} className="text-amber-500" />
                  <span className="text-[10px] text-gray-500 font-semibold">SPEED</span>
                </div>
              </th>
              <th className="py-2 px-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <DollarSign size={12} className="text-green-500" />
                  <span className="text-[10px] text-gray-500 font-semibold">COST</span>
                </div>
              </th>
              {thirdMetric && (
                <th className="py-2 px-1 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Code size={12} className="text-blue-500" />
                    <span className="text-[10px] text-gray-500 font-semibold">{thirdMetric.label.toUpperCase()}</span>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-gray-50/50">
              <td className="py-2.5 px-2">
                <div className="flex items-center gap-1.5">
                  <ModelLogo model={model1} modelName={model1Name} size="xs" />
                  <span className={`text-xs font-medium truncate max-w-[60px] ${model1Failed ? 'text-red-400' : 'text-gray-700'}`}>
                    {model1Name.split(' ')[0]}
                  </span>
                </div>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(speedWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : formatLatency(latency1)}
                </span>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(costWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : formatCost(cost1)}
                </span>
              </td>
              {thirdMetric && (
                <td className="py-2.5 px-1 text-center">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 1, model1Failed)}`}>
                    {model1Failed ? '-' : thirdMetric.value1}
                  </span>
                </td>
              )}
            </tr>
            <tr>
              <td className="py-2.5 px-2">
                <div className="flex items-center gap-1.5">
                  <ModelLogo model={model2} modelName={model2Name} size="xs" />
                  <span className={`text-xs font-medium truncate max-w-[60px] ${model2Failed ? 'text-red-400' : 'text-gray-700'}`}>
                    {model2Name.split(' ')[0]}
                  </span>
                </div>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(speedWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : formatLatency(latency2)}
                </span>
              </td>
              <td className="py-2.5 px-1 text-center">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(costWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : formatCost(cost2)}
                </span>
              </td>
              {thirdMetric && (
                <td className="py-2.5 px-1 text-center">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 2, model2Failed)}`}>
                    {model2Failed ? '-' : thirdMetric.value2}
                  </span>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Desktop version
  return (
    <div
      className="absolute right-0 w-[160px] z-10 pointer-events-auto hidden sm:block"
      style={{
        background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1) 15%)',
        top: '12px',
        bottom: '12px'
      }}
    >
      <div className="ml-4 h-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden flex flex-col">
        <div className="px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex justify-around items-center">
            <div className={model1Failed ? 'opacity-50' : ''}>
              <ModelLogo model={model1} modelName={model1Name} size="sm" />
            </div>
            <div className={model2Failed ? 'opacity-50' : ''}>
              <ModelLogo model={model2} modelName={model2Name} size="sm" />
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col divide-y divide-gray-100">
          <div className="flex-1 p-2 flex flex-col justify-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Zap size={14} className="text-amber-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Speed</span>
            </div>
            <div className="flex justify-around">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(speedWinner === 1, model1Failed)}`}>
                {model1Failed ? '-' : formatLatency(latency1)}
              </span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(speedWinner === 2, model2Failed)}`}>
                {model2Failed ? '-' : formatLatency(latency2)}
              </span>
            </div>
          </div>

          <div className="flex-1 p-2 flex flex-col justify-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <DollarSign size={14} className="text-green-500" />
              <span className="text-xs text-gray-500 font-semibold uppercase">Cost</span>
            </div>
            <div className="flex justify-around">
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(costWinner === 1, model1Failed)}`}>
                {model1Failed ? '-' : formatCost(cost1)}
              </span>
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(costWinner === 2, model2Failed)}`}>
                {model2Failed ? '-' : formatCost(cost2)}
              </span>
            </div>
          </div>

          {thirdMetric && (
            <div className="flex-1 p-2 flex flex-col justify-center">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <AlignJustify size={14} className="text-blue-500" />
                <span className="text-xs text-gray-500 font-semibold uppercase">{thirdMetric.label}</span>
              </div>
              <div className="flex justify-around">
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 1, model1Failed)}`}>
                  {model1Failed ? '-' : thirdMetric.value1}
                </span>
                <span className={`text-sm font-bold px-2 py-0.5 rounded ${getCellStyle(thirdMetricWinner === 2, model2Failed)}`}>
                  {model2Failed ? '-' : thirdMetric.value2}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MOBILE ACTIONS MENU
// ==========================================

function MobileActionsMenu({ comparison, onTogglePrivacy, isOwner }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative flex items-center justify-center">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
        aria-label="More actions"
      >
        <MoreVertical size={16} className="text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50 min-w-[160px]">
            <ComparisonCardActions
              comparison={comparison}
              onTogglePrivacy={isOwner ? onTogglePrivacy : null}
              isOwner={isOwner}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ==========================================
// LOGIN WALL
// ==========================================

function LoginWall({ onDismiss, onLogin }) {
  return (
    <div className="fixed inset-0 bg-gradient-to-b from-transparent via-white/95 to-white z-30 flex items-end justify-center pb-20">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 mx-4 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <LogIn size={32} className="text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          Sign in to see more
        </h3>
        <p className="text-gray-600 mb-6">
          Create a free account to view all comparisons, vote, and run your own benchmarks.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onDismiss}
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Maybe later
          </button>
          <button
            onClick={onLogin}
            className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// COMPACT RESULT CARD (for embedded grid view)
// ==========================================
function CompactResultCard({ run, navigate }) {
  const [isHovered, setIsHovered] = useState(false);
  const [visibleResults, setVisibleResults] = useState(0);
  const models = run.results?.map(r => r.model_name || r.model || r.model_id) || [];
  const results = run.results || [];
  const prompt = run.prompt || run.user_prompt || 'No prompt';
  const category = run.category || '';
  const isImageComparison = category.toLowerCase().includes('image') || category.toLowerCase().includes('video');
  const getImageUrl = (r) => r.image_url || r.output_url || r.url || (r.output && r.output.startsWith('http') ? r.output : null);

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  // Hover animation: reveal results one by one
  useEffect(() => {
    if (!isHovered || results.length === 0) { setVisibleResults(0); return; }
    setVisibleResults(1);
    const interval = setInterval(() => {
      setVisibleResults(prev => prev < results.length ? prev + 1 : prev);
    }, 400);
    return () => clearInterval(interval);
  }, [isHovered, results.length]);

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:border-violet-200 transition-all cursor-pointer overflow-hidden group relative h-[220px]"
      onClick={() => navigate(`/result/${run.run_id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Hover overlay: preview responses */}
      {isHovered && results.length > 0 && (
        <div className="absolute inset-0 bg-white z-10 overflow-y-auto p-3 animate-fadeIn">
          <p className="text-xs text-violet-500 mb-2 font-bold">
            {isImageComparison ? '🖼️ Generated Images:' : '💬 Model Responses:'}
          </p>
          {isImageComparison ? (
            <div className="grid grid-cols-2 gap-2">
              {results.slice(0, visibleResults).map((result, i) => {
                const imageUrl = getImageUrl(result);
                return (
                  <div key={i} className="animate-fadeIn">
                    {imageUrl ? (
                      <div className="relative">
                        <img src={imageUrl} alt="" className="w-full h-20 object-cover rounded-lg" onError={(e) => { e.target.style.display = 'none'; }} />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 rounded-b-lg">
                          <p className="text-[10px] text-white truncate">{result.model_name || result.model}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                        <span className="text-xs text-gray-400">No image</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            results.slice(0, visibleResults).map((result, i) => (
              <div key={i} className={`mb-2 flex items-start gap-2 animate-fadeIn ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                <ModelLogo modelName={result.model_name || result.model} size="sm" />
                <div className={`flex-1 min-w-0 ${i % 2 === 1 ? 'text-right' : ''}`}>
                  <p className="text-xs font-semibold text-violet-700 truncate">{result.model_name || result.model}</p>
                  <p className="text-xs text-gray-600 line-clamp-2">{result.output || result.response || result.content || 'No response'}</p>
                </div>
              </div>
            ))
          )}
          {visibleResults < results.length && (
            <div className="flex items-center gap-1 text-violet-500 mt-2">
              {[0, 1, 2].map(i => (
                <span key={i} className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.1}s` }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Default state */}
      <div className={`h-full flex flex-col transition-opacity ${isHovered && results.length > 0 ? 'opacity-0' : 'opacity-100'}`}>
        {/* Header gradient bar */}
        <div className="h-1 bg-gradient-to-r from-violet-400 to-purple-500 flex-shrink-0" />

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          {/* Category + Date */}
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2 py-0.5 bg-violet-50 rounded-md">
                {getCategoryIcon(category, 14)}
                <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">
                  {category?.replace(/_/g, ' ')?.split(' ').slice(0, 2).join(' ') || 'Compare'}
                </span>
              </div>
              {isImageComparison && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-50 text-pink-600 font-medium">
                  {category.includes('VIDEO') ? '🎬' : '🖼️'}
                </span>
              )}
            </div>
            <span className="text-xs text-gray-400">{formatDateShort(run.created_at)}</span>
          </div>

          {/* Prompt */}
          <p className="font-semibold text-gray-900 text-sm line-clamp-2 mb-3 group-hover:text-violet-800 transition-colors leading-relaxed">
            {prompt}
          </p>

          {/* Models */}
          <div className="flex flex-wrap gap-1.5 mb-3 flex-1 content-start">
            {models.slice(0, 4).map((model, i) => (
              <div key={i} className="flex items-center gap-1 bg-gray-50 rounded-full px-2 py-0.5 border border-gray-100">
                <ModelLogo modelName={model} size="xs" />
                <span className="text-xs text-gray-600 truncate max-w-[60px]">
                  {String(model).split(' ')[0]}
                </span>
              </div>
            ))}
            {models.length > 4 && (
              <span className="text-xs text-gray-400 px-2 py-0.5">+{models.length - 4}</span>
            )}
          </div>

          {/* Footer stats */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs text-gray-400 flex-shrink-0">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <User size={12} />
                {run.username || 'Anonymous'}
              </span>
              {run.results && (
                <span className="text-violet-500 font-medium">{run.results.length} models</span>
              )}
            </div>
            <span className="flex items-center gap-1 text-violet-400 group-hover:text-violet-600 transition-colors font-medium">
              View <ChevronRight size={14} />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// MAIN COMPONENT
// ==========================================

function ResultsHome({ user, onOpenLogin, embedded = false, onTotalChange }) {
  const { categories } = useCategories();
  const { models: availableModels } = useModels();

  const {
    results,
    setResults,
    loading,
    loadingMore,
    hasMore,
    filter,
    setFilter,
    categoryFilter,
    setCategoryFilter,
    modelFilter,
    setModelFilter,
    sortFilter,
    setSortFilter,
    votes,
    setVotes,
    userVoted,
    setUserVoted,
    loadResults,
    isCacheValid
  } = useResults();

  // Local UI state
  const [showStats, setShowStats] = useState(new Set());
  const [imageModal, setImageModal] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState(new Set());
  const [loginWallDismissed, setLoginWallDismissed] = useState(true); // PORTFOLIO: always dismissed
  const [showLoginWall, setShowLoginWall] = useState(false);

  // HIDDEN: Comments feature disabled
  // const [commentsModal, setCommentsModal] = useState(null);
  // const [commentCounts, setCommentCounts] = useState({});

  const navigate = useNavigate();

  // Report total count up to parent (for tab counter)
  useEffect(() => {
    if (onTotalChange && results.length > 0) {
      onTotalChange(results.length);
    }
  }, [results.length, onTotalChange]);

  // ✅ Scroll to specific benchmark (from Community Feed)
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedBenchmark = searchParams.get('benchmark');

  const cardRefs = useRef({});
  const RESULTS_PER_PAGE = 10;

  // ==========================================
  // HELPER FUNCTIONS
  // ==========================================

  const findModel = useCallback((modelName) => {
    if (!modelName || !availableModels) return null;
    const name = modelName.toLowerCase();
    return availableModels.find(m =>
      m.id.toLowerCase() === name ||
      m.name.toLowerCase().includes(name) ||
      name.includes(m.id.toLowerCase())
    );
  }, [availableModels]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // HIDDEN: Comments feature disabled
  /*
  // Load comment counts for visible benchmarks
  const loadCommentCounts = useCallback(async (runIds) => {
    try {
      const counts = {};
      await Promise.all(runIds.map(async (runId) => {
        const res = await fetch(`${API_URL}/comments/count/${runId}`);
        if (res.ok) {
          const data = await res.json();
          counts[runId] = data.count;
        }
      }));
      setCommentCounts(prev => ({ ...prev, ...counts }));
    } catch (e) {
      console.error('Failed to load comment counts:', e);
    }
  }, []);

  // Callback when comment is added - update count immediately
  const handleCommentAdded = useCallback((benchmarkId) => {
    setCommentCounts(prev => ({
      ...prev,
      [benchmarkId]: (prev[benchmarkId] || 0) + 1
    }));
  }, []);

  // Callback when comment is deleted - update count immediately
  const handleCommentDeleted = useCallback((benchmarkId) => {
    setCommentCounts(prev => ({
      ...prev,
      [benchmarkId]: Math.max((prev[benchmarkId] || 1) - 1, 0)
    }));
  }, []);
  */

  // ==========================================
  // EFFECTS
  // ==========================================

  useEffect(() => {
    if (!isCacheValid()) {
      loadResults(true);
    }
  }, [filter, categoryFilter, modelFilter, sortFilter]);

  // HIDDEN: Comments feature disabled
  /*
  // Load comment counts when results change
  useEffect(() => {
    if (results.length > 0) {
      const runIds = results.map(r => r.run_id).filter(Boolean);
      loadCommentCounts(runIds);
    }
  }, [results, loadCommentCounts]);
  */

  // ✅ Scroll to specific benchmark when coming from Community Feed
  useEffect(() => {
    if (highlightedBenchmark && results.length > 0) {
      const element = document.getElementById(`benchmark-${highlightedBenchmark}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Brief highlight effect
          element.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.5)';
          setTimeout(() => {
            element.style.boxShadow = '';
            setSearchParams({});
          }, 2000);
        }, 500);
      }
    }
  }, [highlightedBenchmark, results, setSearchParams]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore || showLoginWall) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 500) {
        loadResults(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, loadingMore, hasMore, loadResults, showLoginWall]);

  useEffect(() => {
    if (!user && !loginWallDismissed && results.length >= LOGIN_WALL_THRESHOLD) {
      setShowLoginWall(true);
    }
  }, [results.length, user, loginWallDismissed]);

  useEffect(() => {
    results.forEach(run => {
      if (run.run_id) trackComparisonView(run.run_id, run.category);
    });
  }, [results]);

  // ==========================================
  // VOTE HANDLING
  // ==========================================

  const handleVote = async (resultId, runId, modelName) => {
    if (votingInProgress.has(resultId)) return;

    if (!user) {
      showToast('Please log in to vote', 'info');
      if (onOpenLogin) onOpenLogin();
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    setVotingInProgress(prev => new Set(prev).add(resultId));

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const wasVoted = userVoted.has(resultId);
    const run = results.find(r => r.run_id === runId);

    setUserVoted(prev => {
      const newSet = new Set(prev);
      if (wasVoted) {
        newSet.delete(resultId);
      } else {
        if (run && run.results) {
          run.results.forEach(result => {
            if (result.id !== resultId) {
              newSet.delete(result.id);
            }
          });
        }
        newSet.add(resultId);
      }
      return newSet;
    });

    try {
      const response = await fetch(`${API_URL}/benchmark/vote/${resultId}`, {
        method: wasVoted ? 'DELETE' : 'POST',
        headers
      });

      if (response.ok) {
        trackVote(modelName, 'results_page');
        const data = await response.json();

        if (data.all_votes) {
          const parsedVotes = {};
          Object.entries(data.all_votes).forEach(([id, voteData]) => {
            const voteNum = typeof voteData === 'object'
              ? (typeof voteData.votes === 'number' ? voteData.votes : parseInt(voteData.votes, 10) || 0)
              : (typeof voteData === 'number' ? voteData : parseInt(voteData, 10) || 0);
            parsedVotes[id] = { votes: voteNum };
          });
          setVotes(prev => ({
            ...prev,
            [runId]: parsedVotes
          }));
        }
      } else {
        setUserVoted(prev => {
          const newSet = new Set(prev);
          if (wasVoted) newSet.add(resultId);
          else newSet.delete(resultId);
          return newSet;
        });
        if (response.status === 401) {
          showToast('Please log in to vote', 'warning');
        } else {
          showToast('Vote failed. Please try again.', 'error');
        }
      }
    } catch (error) {
      console.error('Vote error:', error);
      setUserVoted(prev => {
        const newSet = new Set(prev);
        if (wasVoted) newSet.add(resultId);
        else newSet.delete(resultId);
        return newSet;
      });
      showToast('Connection error. Please try again.', 'error');
    } finally {
      setVotingInProgress(prev => {
        const newSet = new Set(prev);
        newSet.delete(resultId);
        return newSet;
      });
    }
  };

  // ==========================================
  // TOGGLE PRIVACY
  // ==========================================

  const togglePrivacy = async (runId, currentIsPublic) => {
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('Please log in to change privacy', 'warning');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/benchmark/${runId}/privacy`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_public: !currentIsPublic })
      });

      if (response.ok) {
        setResults(prev => prev.map(run =>
          run.run_id === runId ? { ...run, is_public: !currentIsPublic } : run
        ));
        trackPrivacyToggle(runId, !currentIsPublic);
        showToast(`Comparison is now ${!currentIsPublic ? 'public' : 'private'}`, 'success');
      } else {
        showToast('Failed to update privacy', 'error');
      }
    } catch (error) {
      console.error('Privacy toggle error:', error);
      showToast('Failed to update privacy', 'error');
    }
  };

  // ==========================================
  // FILTER HANDLERS
  // ==========================================

  const handleFilterChange = (val) => {
    setFilter(val);
    trackFilter('visibility', val);
  };

  const handleCategoryFilterChange = (val) => {
    setCategoryFilter(val);
    trackFilter('category', val);
  };

  const handleModelFilterChange = (val) => {
    setModelFilter(val);
    trackFilter('model', val);
  };

  const handleSortChange = (val) => {
    setSortFilter(val);
    trackSort(val);
  };

  // ==========================================
  // IMAGE FUNCTIONS
  // ==========================================

  const openImageModal = (url, model) => {
    setImageModal({ url, model });
  };

  const downloadImage = async (url, modelName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${modelName.replace(/\s+/g, '_')}_output.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      trackDownload(modelName);
    } catch (error) {
      console.error('Download error:', error);
      showToast('Failed to download image', 'error');
    }
  };

  // ==========================================
  // SORTING
  // ==========================================

  const sortedResults = [...results].sort((a, b) => {
    switch (sortFilter) {
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'most_voted':
        const aVotes = Object.values(votes[a.run_id] || {}).reduce((sum, v) => sum + (v.votes || 0), 0);
        const bVotes = Object.values(votes[b.run_id] || {}).reduce((sum, v) => sum + (v.votes || 0), 0);
        return bVotes - aVotes;
      case 'least_voted':
        const aVotesL = Object.values(votes[a.run_id] || {}).reduce((sum, v) => sum + (v.votes || 0), 0);
        const bVotesL = Object.values(votes[b.run_id] || {}).reduce((sum, v) => sum + (v.votes || 0), 0);
        return aVotesL - bVotesL;
      case 'newest':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  const displayResults = sortedResults;

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <main className={`${embedded ? '' : 'min-h-screen'} pb-4 sm:pb-8`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-3">

        {/* Mobile Header */}
        <div className="sm:hidden flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-100 p-2">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg"
            aria-expanded={showMobileFilters}
            aria-controls="mobile-filters-panel"
            aria-label="Toggle filters menu"
          >
            <Filter size={16} className="text-gray-600" aria-hidden="true" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
            <ChevronDown
              size={14}
              className={`text-gray-400 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>

          <button
            onClick={() => {
              if (showStats.size > 0) {
                setShowStats(new Set());
              } else {
                const allRunIds = new Set(results.map(r => r.run_id));
                setShowStats(allRunIds);
              }
            }}
            className={`h-9 px-3 flex items-center gap-1.5 rounded-lg border transition-all ${
              showStats.size > 0
                ? 'bg-blue-50 border-blue-300'
                : 'bg-gray-50 border-gray-200'
            }`}
            aria-label={showStats.size > 0 ? 'Hide all statistics' : 'Show all statistics'}
            aria-pressed={showStats.size > 0}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <rect x="1" y="8" width="3" height="7" rx="1" fill="#93C5FD" />
              <rect x="6" y="4" width="3" height="11" rx="1" fill="#86EFAC" />
              <rect x="11" y="1" width="3" height="14" rx="1" fill="#FCA5A5" />
            </svg>
            <span className="text-xs font-medium text-gray-600">{showStats.size > 0 ? 'ON' : 'Stats'}</span>
          </button>
        </div>

        {/* Mobile Filters */}
        <div
          id="mobile-filters-panel"
          className={`sm:hidden overflow-visible transition-all duration-300 ${
showMobileFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden pointer-events-none'          }`}
        >
          <div className="grid grid-cols-2 gap-2 bg-white rounded-xl p-3 border border-gray-100">
            <CustomDropdown
              value={filter}
              onChange={handleFilterChange}
              placeholder="All Results"
              className="w-full"
              dropdownClassName="min-w-[180px] left-0"
              options={[
                { value: 'all', label: 'All Results' },
                { value: 'public', label: 'Public' },
                ...(user ? [{ value: 'mine', label: 'My Runs' }] : [])
              ]}
            />
            <CustomDropdown
              value={categoryFilter}
              onChange={handleCategoryFilterChange}
              type="category"
              placeholder="Category"
              className="w-full"
              dropdownClassName="min-w-[200px] right-0"
              options={[
                { value: 'all', label: 'All Categories' },
                ...categories.map(cat => ({
                  value: cat.id,
                  label: cat.name,
                  icon: getCategoryIcon(cat.id, 16)
                }))
              ]}
            />
            <CustomDropdown
              value={modelFilter}
              onChange={handleModelFilterChange}
              type="model"
              placeholder="Model"
              className="w-full"
              dropdownClassName="min-w-[220px] left-0"
              options={[
                { value: 'all', label: 'All Models' },
                ...availableModels.map(m => ({
                  value: m.id,
                  label: m.name,
                  model: m
                }))
              ]}
            />
            <CustomDropdown
              value={sortFilter}
              onChange={handleSortChange}
              placeholder="Sort"
              className="w-full"
              dropdownClassName="min-w-[160px] right-0"
              options={[
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
                { value: 'most_voted', label: 'Most Voted' },
                { value: 'least_voted', label: 'Least Voted' }
              ]}
            />
          </div>
        </div>

        {/* Desktop Filters */}
        <nav className="hidden sm:flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-100 p-3">
          <div className="flex items-center gap-3">
            <CustomDropdown
              value={filter}
              onChange={handleFilterChange}
              placeholder="All"
              className="w-auto min-w-[100px]"
              options={[
                { value: 'all', label: 'All Results' },
                { value: 'public', label: 'Public' },
                ...(user ? [{ value: 'mine', label: 'My Runs' }] : [])
              ]}
            />
            <CustomDropdown
              value={categoryFilter}
              onChange={handleCategoryFilterChange}
              type="category"
              placeholder="Category"
              className="w-auto min-w-[160px]"
              dropdownClassName="min-w-[200px]"
              options={[
                { value: 'all', label: 'All Categories' },
                ...categories.map(cat => ({
                  value: cat.id,
                  label: cat.name,
                  icon: getCategoryIcon(cat.id, 16)
                }))
              ]}
            />
            <CustomDropdown
              value={modelFilter}
              onChange={handleModelFilterChange}
              type="model"
              placeholder="Model"
              className="w-auto min-w-[140px]"
              dropdownClassName="min-w-[220px] right-0"
              options={[
                { value: 'all', label: 'All Models' },
                ...availableModels.map(m => ({
                  value: m.id,
                  label: m.name,
                  model: m
                }))
              ]}
            />
            <CustomDropdown
              value={sortFilter}
              onChange={handleSortChange}
              placeholder="Newest"
              className="w-auto min-w-[140px]"
              options={[
                { value: 'newest', label: 'Newest' },
                { value: 'oldest', label: 'Oldest' },
                { value: 'most_voted', label: 'Most Voted' },
                { value: 'least_voted', label: 'Least Voted' }
              ]}
            />
          </div>

          <StatsToggle
            showStats={showStats.size > 0}
            onToggle={() => {
              if (showStats.size > 0) {
                setShowStats(new Set());
              } else {
                const allRunIds = new Set(results.map(r => r.run_id));
                setShowStats(allRunIds);
              }
            }}
          />
        </nav>

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader className="animate-spin h-12 w-12 text-blue-600 mb-4" />
            <p className="text-gray-600 font-medium">Loading comparisons...</p>
          </div>
        ) : displayResults.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Info size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No comparisons found</h3>
            <p className="text-gray-600">Try adjusting your filters or run a new comparison.</p>
          </div>
        ) : (
          <>
            {/* Results - Compact Grid (embedded) or Full Feed */}
            {embedded ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {displayResults.map((run) => {
                  if (!run || !run.run_id) return null;
                  return <CompactResultCard key={run.run_id} run={run} navigate={navigate} />;
                })}
              </div>
            ) : (
            <div className="space-y-4">
              {displayResults.map((run) => {
                if (!run || !run.run_id) return null;

                const isImageGen = run.category === 'IMAGE_GENERATION';
                const isVideoGen = run.category === 'IMAGE_TO_VIDEO' || run.category === 'VIDEO_GENERATION' || run.category === 'TEXT_TO_VIDEO';
                const isCodeGen = run.category === 'CODE_GENERATION';

                if (!cardRefs.current[run.run_id]) {
                  cardRefs.current[run.run_id] = React.createRef();
                }

                return (
                  <article
                    key={run.run_id}
                    id={`benchmark-${run.run_id}`}
                    ref={cardRefs.current[run.run_id]}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden border-l-4 border-l-blue-200 transition-shadow duration-300"
                  >
                    {/* Header */}
                    <div className="px-3 sm:px-4 py-3 border-b border-gray-100">
                      {/* Desktop Header */}
                      <div className="hidden sm:flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 px-2.5 py-1 bg-gray-50 rounded-lg">
                            {getCategoryIcon(run.category, 16)}
                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              {run.category?.replace(/_/g, ' ') || 'Unknown'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">{formatDate(run.created_at)}</span>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <User size={14} />
                            <span>{run.username || 'Anonymous'}</span>
                          </div>
                          {run.is_owner && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-medium rounded">
                              Yours
                            </span>
                          )}
                          {run.is_public !== undefined && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 ${
                              run.is_public
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {run.is_public ? <Unlock size={10} /> : <Lock size={10} />}
                              {run.is_public ? 'Public' : 'Private'}
                            </span>
                          )}

                          {/* Stats Button */}
                          {run.results && run.results.length >= 2 && (
                            <button
                              onClick={() => {
                                setShowStats(prev => {
                                  const next = new Set(prev);
                                  if (next.has(run.run_id)) {
                                    next.delete(run.run_id);
                                  } else {
                                    next.add(run.run_id);
                                  }
                                  return next;
                                });
                              }}
                              className={`p-1.5 rounded-lg transition-all ${
                                showStats.has(run.run_id)
                                  ? 'bg-blue-50'
                                  : 'hover:bg-gray-100'
                              }`}
                              title={showStats.has(run.run_id) ? 'Hide stats' : 'Show stats'}
                            >
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                                <rect x="1" y="8" width="3" height="7" rx="1" fill="#93C5FD" />
                                <rect x="6" y="4" width="3" height="11" rx="1" fill="#86EFAC" />
                                <rect x="11" y="1" width="3" height="14" rx="1" fill="#FCA5A5" />
                              </svg>
                            </button>
                          )}

                          {/* HIDDEN: Comments Button - Desktop
                          <button
                            onClick={() => setCommentsModal({
                              benchmarkId: run.run_id,
                              info: { category: run.category, prompt: run.prompt }
                            })}
                            className="flex items-center gap-1.5 px-2 py-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View comments"
                          >
                            <MessageCircle size={16} />
                            {commentCounts[run.run_id] > 0 && (
                              <span className="text-xs font-medium">{formatNumber(commentCounts[run.run_id])}</span>
                            )}
                          </button>
                          */}

                          <ComparisonCardActions
                            comparison={run}
                            onTogglePrivacy={run.is_owner ? () => togglePrivacy(run.run_id, run.is_public) : null}
                            isOwner={run.is_owner}
                          />
                        </div>
                      </div>

                      {/* Mobile Header */}
                      <div className="sm:hidden">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg">
                              {getCategoryIcon(run.category, 14)}
                              <span className="text-xs font-semibold text-gray-700 uppercase">
                                {run.category?.replace(/_/g, ' ')?.split(' ')[0] || 'Unknown'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-400">{formatDate(run.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Stats Button - Mobile */}
                            {run.results && run.results.length >= 2 && (
                              <button
                                onClick={() => {
                                  setShowStats(prev => {
                                    const next = new Set(prev);
                                    if (next.has(run.run_id)) {
                                      next.delete(run.run_id);
                                    } else {
                                      next.add(run.run_id);
                                    }
                                    return next;
                                  });
                                }}
                                className={`w-8 h-8 rounded-lg transition-all flex items-center justify-center ${
                                  showStats.has(run.run_id)
                                    ? 'bg-blue-50'
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <rect x="1" y="8" width="3" height="7" rx="1" fill="#93C5FD" />
                                  <rect x="6" y="4" width="3" height="11" rx="1" fill="#86EFAC" />
                                  <rect x="11" y="1" width="3" height="14" rx="1" fill="#FCA5A5" />
                                </svg>
                              </button>
                            )}

                            {/* HIDDEN: Comments Button - Mobile
                            <button
                              onClick={() => setCommentsModal({
                                benchmarkId: run.run_id,
                                info: { category: run.category, prompt: run.prompt }
                              })}
                              className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-blue-50 flex items-center justify-center text-gray-500 hover:text-blue-600 relative"
                            >
                              <MessageCircle size={16} />
                              {commentCounts[run.run_id] > 0 && (
                                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                                  {commentCounts[run.run_id] > 9 ? '9+' : commentCounts[run.run_id]}
                                </span>
                              )}
                            </button>
                            */}

                            <MobileActionsMenu
                              comparison={run}
                              onTogglePrivacy={() => togglePrivacy(run.run_id, run.is_public)}
                              isOwner={run.is_owner}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <User size={12} />
                          <span>{run.username || 'Anonymous'}</span>
                          {run.is_owner && (
                            <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-xs font-medium rounded">
                              Yours
                            </span>
                          )}
                          {run.is_public !== undefined && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              run.is_public
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              {run.is_public ? 'Public' : 'Private'}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 flex gap-3 items-start">
                        {run.input_image_url && ['IMAGE_TO_VIDEO', 'IMAGE_ANALYSIS', 'VISION'].includes(run.category) && (
                          <img
                            src={run.input_image_url}
                            alt="Input"
                            className="w-12 h-12 rounded-lg object-cover flex-shrink-0 border border-gray-200"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <ExpandablePrompt prompt={run.prompt} maxLength={100} />
                        </div>
                      </div>
                    </div>

                    {/* Results Grid */}
                    <div className="p-3 sm:p-4 relative">
                      {showStats.has(run.run_id) && run.results && run.results.length >= 2 && (
                        <div className="sm:hidden">
                          <StatsOverlay
                            results={run.results}
                            category={run.category}
                            findModel={findModel}
                            isMobile={true}
                          />
                        </div>
                      )}

                      {!run.results || run.results.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>No model results for this comparison</p>
                        </div>
                      ) : (
                        <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 relative transition-all ${showStats.has(run.run_id) ? 'sm:pr-[170px]' : ''}`}>
                          {run.results.map((result, idx) => {
                            if (!result) return null;

                            const modelName = result.model || result.model_name || result.model_id || 'Unknown';
                            const resultVotes = votes[run.run_id]?.[result.id]?.votes || 0;
                            const myVote = userVoted.has(result.id);

                            return (
                              <div
                                key={result.id || idx}
                                className="flex flex-col bg-white rounded-xl p-3 border border-gray-200 shadow-sm"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <ModelLogo model={findModel(modelName)} modelName={modelName} size="sm" />
                                    <h3 className="font-bold text-sm truncate">{modelName}</h3>
                                  </div>

                                  <button
                                    onClick={() => handleVote(result.id, run.run_id, modelName)}
                                    disabled={votingInProgress.has(result.id)}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all ${
                                      myVote
                                        ? 'bg-green-100 text-green-700 font-bold shadow-sm scale-105'
                                        : resultVotes > 0
                                          ? 'bg-green-50 text-green-600'
                                          : 'text-gray-400 hover:text-green-500 hover:bg-gray-50'
                                    } ${votingInProgress.has(result.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    aria-label={myVote ? `Remove vote from ${modelName}` : `Vote for ${modelName}`}
                                    aria-pressed={myVote}
                                    title={myVote ? 'Remove vote' : 'Vote for this result'}
                                  >
                                    <Star size={18} fill={myVote || resultVotes > 0 ? 'currentColor' : 'none'} aria-hidden="true" />
                                    {resultVotes > 0 && (
                                      <span className="text-sm font-medium">{formatNumber(resultVotes)}</span>
                                    )}
                                  </button>
                                </div>

                                <div className="flex-1 min-h-0">
                                  {isVideoGen ? (
                                    <div className="h-[240px] sm:h-[280px]">
                                      <VideoPlayer videoUrl={
                                        (result.video_url && result.video_url.startsWith('http')) ? result.video_url :
                                        (result.output_video_url && result.output_video_url.startsWith('http')) ? result.output_video_url :
                                        null
                                      } />
                                    </div>
                                  ) : isImageGen ? (
                                    <div
                                      className="relative rounded-lg overflow-hidden border border-gray-100 cursor-pointer h-[240px] sm:h-[280px]"
                                      onClick={() => openImageModal(result.image_url || result.response, modelName)}
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`View full image generated by ${modelName}`}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          openImageModal(result.image_url || result.response, modelName);
                                        }
                                      }}
                                    >
                                      <img
                                        src={result.image_url || result.response}
                                        alt={`AI-generated image by ${modelName}`}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                        width={400}
                                        height={280}
                                      />
                                    </div>
                                  ) : isCodeGen ? (
                                    <div className="bg-gray-900 rounded-lg p-2.5 h-[240px] sm:h-[280px] overflow-y-auto relative">
                                      <CopyButton text={result.response || ''} className="absolute top-2 right-2 z-10" />
                                      <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words font-mono pr-8">
                                        {result.response || 'No response'}
                                      </pre>
                                    </div>
                            ) : (
                              <div className="bg-gray-50 rounded-lg p-2.5 h-[240px] sm:h-[280px] overflow-y-auto relative group">
                                <button
                                  onClick={async () => {
                                    await navigator.clipboard.writeText(result.response || '');
                                    showToast('Copied to clipboard!', 'success');
                                  }}
                                  className="absolute top-2 right-2 z-10 p-1.5 rounded bg-white border border-gray-200 hover:bg-gray-100 transition-colors opacity-0 group-hover:opacity-100"
                                  title="Copy text"
                                >
                                  <Copy size={14} className="text-gray-500" />
                                </button>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words pr-8">
                                  {result.response || 'No response'}
                                </p>
                              </div>
                            )}
                                </div>
                              </div>
                            );
                          })}

                          {showStats.has(run.run_id) && run.results && run.results.length >= 2 && (
                            <StatsOverlay
                              results={run.results}
                              category={run.category}
                              findModel={findModel}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
            )}

            {showLoginWall && !user && (
              <LoginWall
                onDismiss={() => {
                  setLoginWallDismissed(true);
                  setShowLoginWall(false);
                }}
                onLogin={() => onOpenLogin && onOpenLogin()}
              />
            )}

            {loadingMore && (
              <div className="flex items-center justify-center py-8">
                <Loader className="animate-spin h-8 w-8 text-blue-600" />
                <span className="ml-3 text-gray-600 font-medium">Loading more results...</span>
              </div>
            )}

            {!hasMore && displayResults.length > 0 && !showLoginWall && (
              <div className="text-center py-8 text-gray-500">
                <p className="font-medium">No more results to load</p>
              </div>
            )}
          </>
        )}

        {/* Image Modal */}
        {imageModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setImageModal(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`Full size image by ${imageModal.model}`}
          >
            <div className="bg-white rounded-xl p-4 sm:p-6 max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl sm:text-2xl font-bold">{imageModal.model}</h3>
                <button
                  onClick={() => setImageModal(null)}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close image preview"
                >
                  <X size={24} aria-hidden="true" />
                </button>
              </div>
              <img
                src={imageModal.url}
                alt={`Full size AI-generated image by ${imageModal.model}`}
                className="w-full h-auto rounded-lg mb-4"
                width={800}
                height={800}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => downloadImage(imageModal.url, imageModal.model)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                  aria-label={`Download image generated by ${imageModal.model}`}
                >
                  <Download size={18} aria-hidden="true" />
                  Download Image
                </button>
              </div>
            </div>
          </div>
        )}

        {/* HIDDEN: Comments Modal
        {commentsModal && (
          <CommentsModal
            isOpen={!!commentsModal}
            onClose={() => setCommentsModal(null)}
            benchmarkId={commentsModal.benchmarkId}
            benchmarkInfo={commentsModal.info}
            user={user}
            onLoginClick={() => onOpenLogin && onOpenLogin()}
            onCommentAdded={() => handleCommentAdded(commentsModal.benchmarkId)}
            onCommentDeleted={() => handleCommentDeleted(commentsModal.benchmarkId)}
          />
        )}
        */}
      </div>

      {/* CSS for hover animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out forwards;
        }
      `}</style>
    </main>
  );
}

export default ResultsHome;